import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
/* eslint-disable  import/no-cycle */
import { RootState } from 'store/rootReducer';
import { ApiErrors, Loaders } from 'api/types';
import keyBy from 'lodash/keyBy';
import set from 'lodash/set';
import { UNAUTHORIZED, FORBIDDEN, NOT_FOUND } from 'http-status-codes';
import { i18n } from 'helpers/i18n';
import { axiosInstance } from '../axiosInstance';

export type Site = {
  code: string;
  country_code: string;
  created_at: string;
  deleted_at: string;
  id: string;
  name: string;
  time_zone: string;
  updated_at: string;
  user_id: string;
};

type SiteState = {
  apiErrors: ApiErrors;
  list: {
    [key: string]: Site;
  };
  loaders: Loaders;
  timezones: string[];
};

const sitesApiPrefix = '/sites';

// TODO: use object instead of  array
const initialState: SiteState = {
  apiErrors: {
    loadingError: '',
    savingError: ''
  },
  list: {},
  loaders: {
    isSaving: false,
    isDeletingItems: {}
  },
  timezones: []
};

export const fetchProductionSites = createAsyncThunk<Site[], null, { rejectValue: string }>(
  'sites/fetchProductionSites',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(sitesApiPrefix);
      const {
        data: { production_sites }
      } = response;
      return production_sites;
    } catch (err) {
      if (!err.response) {
        return rejectWithValue(i18n.t('errors.something went wrong'));
      }
      const { status } = err.response;
      if (status === UNAUTHORIZED || status === FORBIDDEN) {
        return rejectWithValue(i18n.t('errors.unauthorized'));
      }
      return rejectWithValue(i18n.t('errors.something went wrong'));
    }
  }
);

export const fetchSitesTimezones = createAsyncThunk<string[], null, { rejectValue: string }>(
  'sites/fetchSitesTimezones',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${sitesApiPrefix}/timezones`);
      const {
        data: { time_zones }
      } = response;
      return time_zones;
    } catch (err) {
      if (!err.response) {
        return rejectWithValue(i18n.t('errors.something went wrong'));
      }
      const { status } = err.response;
      if (status === UNAUTHORIZED || status === FORBIDDEN) {
        return rejectWithValue(i18n.t('errors.unauthorized'));
      }
      return rejectWithValue(i18n.t('errors.something went wrong'));
    }
  }
);

export const deleteSite = createAsyncThunk<string, string, { rejectValue: string }>(
  'sites/deleteSite',
  async (siteId, { dispatch, rejectWithValue }) => {
    try {
      dispatch(updateListDelete({ id: siteId, value: true }));
      await axiosInstance.delete(`${sitesApiPrefix}/${siteId}`);
      return siteId;
    } catch (err) {
      if (!err.response) {
        return rejectWithValue(i18n.t('errors.something went wrong'));
      }
      const { status } = err.response;
      switch (status) {
        case FORBIDDEN:
        case UNAUTHORIZED:
          return rejectWithValue(i18n.t('sites.you cannot delete this site'));
        case NOT_FOUND:
          return rejectWithValue(i18n.t('sites.not found'));
        default:
          return rejectWithValue(i18n.t('errors.something went wrong'));
      }
    } finally {
      dispatch(updateListDelete({ id: siteId, value: false }));
    }
  }
);

interface SaveSiteParams {
  id?: string;
  site: {
    code: string;
    country_code: string;
    name: string;
    time_zone: string;
  };
}

export const saveSite = createAsyncThunk<Site, SaveSiteParams, { rejectValue: string }>(
  'sites/saveSite',
  async ({ site, id }, { rejectWithValue }) => {
    let response;
    try {
      if (id) {
        response = await axiosInstance.put(`${sitesApiPrefix}/${id}`, site);
      } else {
        response = await axiosInstance.post(sitesApiPrefix, site);
      }
      const { data } = response;
      return data;
    } catch (err) {
      if (!err.response) {
        return rejectWithValue(i18n.t('errors.something went wrong'));
      }
      const { status } = err.response;
      if (status === UNAUTHORIZED || status === FORBIDDEN) {
        return rejectWithValue(i18n.t('sites.you cannot update or create site'));
      }

      return rejectWithValue(i18n.t('errors.something went wrong'));
    }
  }
);

const sitesSlice = createSlice({
  initialState,
  name: 'sites',
  reducers: {
    updateListDelete: (state, { payload }: PayloadAction<{ id: string; value: boolean }>) => {
      const { id, value } = payload;
      if (value) {
        set(state, `loaders.deletingErrorItems.${id}`, '');
      }
      set(state, `loaders.isDeletingItems.${id}`, value);
    },
    resetProductionSites: state => initialState,
    closeError: state => {
      state.apiErrors = {};
    }
  },
  extraReducers: builder =>
    builder
      .addCase(fetchProductionSites.fulfilled, (state, { payload }) => {
        state.loaders.isLoading = false;
        state.list = keyBy(payload, 'id');
      })
      .addCase(fetchProductionSites.pending, state => {
        state.loaders.isLoading = true;
        state.apiErrors.loadingError = '';
      })
      .addCase(fetchProductionSites.rejected, (state, { payload }) => {
        state.loaders.isLoading = false;
        state.apiErrors.loadingError = payload || i18n.t('errors.something went wrong');
      })
      .addCase(fetchSitesTimezones.fulfilled, (state, { payload }) => {
        state.loaders.isLoading = false;
        state.timezones = payload;
      })
      .addCase(fetchSitesTimezones.pending, state => {
        state.loaders.isLoading = true;
        state.apiErrors.loadingError = '';
      })
      .addCase(fetchSitesTimezones.rejected, (state, { payload }) => {
        state.loaders.isLoading = false;
        state.apiErrors.loadingError = payload || i18n.t('errors.something went wrong');
      })
      .addCase(deleteSite.fulfilled, (state, { payload }) => {
        delete state.list[`${payload}`];
      })
      .addCase(saveSite.fulfilled, (state, { payload }) => {
        state.loaders.isSaving = false;
        state.list[payload.id] = payload;
      })
      .addCase(saveSite.pending, state => {
        state.loaders.isSaving = true;
        state.apiErrors.savingError = '';
      })
      .addCase(saveSite.rejected, (state, { payload }) => {
        state.loaders.isSaving = false;
        state.apiErrors.savingError = payload || i18n.t('errors.something went wrong');
      })
});

const { actions, reducer } = sitesSlice;
export { reducer as sites };
export const { closeError, updateListDelete, resetProductionSites } = actions;

export const getSites = (state: RootState) => state.sites.list;
export const getSitesTimezones = (state: RootState) => state.sites.timezones;
export const getSitesLoaders = (state: RootState) => state.sites.loaders;
export const getSitesApiErrors = (state: RootState) => state.sites.apiErrors;
